import { useCallback, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthState } from "@/auth/AuthProvider";
import { fetchBillingSummary } from "./api";

type BillingSummary = Awaited<ReturnType<typeof fetchBillingSummary>>;

type BillingState = {
  loading: boolean;
  error: string | null;
  summary: BillingSummary | null;
  customerId: string | null;
  subscriptionId: string | null;
  plan: string | null;
};

export function useBillingSummary() {
  const { user } = useAuthState();
  const [state, setState] = useState<BillingState>({
    loading: true,
    error: null,
    summary: null,
    customerId: null,
    subscriptionId: null,
    plan: null,
  });

  const load = useCallback(async () => {
    if (!user) {
      setState({ loading: false, error: null, summary: null, customerId: null, subscriptionId: null, plan: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    const harness =
      typeof window !== "undefined"
        ? ((window as typeof window & {
            __billingSummaryStub?: {
              customerId: string;
              subscriptionId: string;
              plan: string | null;
              summary: BillingSummary;
            };
          }).__billingSummaryStub ?? null)
        : null;

    if (harness) {
      setState({
        loading: false,
        error: null,
        summary: harness.summary,
        customerId: harness.customerId,
        subscriptionId: harness.subscriptionId,
        plan: harness.plan,
      });
      return;
    }

    let customerId: string | null = null;
    let subscriptionId: string | null = null;
    let plan: string | null = null;

    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      type SubscriptionRecord = {
        stripeCustomerId?: unknown;
        stripeSubscriptionId?: unknown;
        plan?: unknown;
      };
      const subscription = snap.get("subscription") as SubscriptionRecord | undefined;
      customerId = typeof subscription?.stripeCustomerId === "string" ? subscription.stripeCustomerId : null;
      subscriptionId = typeof subscription?.stripeSubscriptionId === "string" ? subscription.stripeSubscriptionId : null;
      plan = typeof subscription?.plan === "string" ? subscription.plan : null;

      // Fallback: if users/{uid} has no stripeCustomerId, check billingCustomers/{uid}
      // which is the webhook-synced source of truth for Stripe data.
      if (!customerId) {
        const billingSnap = await getDoc(doc(db, "billingCustomers", user.uid));
        if (billingSnap.exists()) {
          const bData = billingSnap.data();
          customerId = typeof bData?.stripeCustomerId === "string" ? bData.stripeCustomerId : null;
          subscriptionId = subscriptionId ?? (typeof bData?.stripeSubscriptionId === "string" ? bData.stripeSubscriptionId : null);
          plan = plan ?? (typeof bData?.subscriptionType === "string" ? bData.subscriptionType : null);
        }
      }

      if (!customerId) {
        setState({ loading: false, error: null, summary: null, customerId: null, subscriptionId, plan });
        return;
      }

      const summary = await fetchBillingSummary(customerId);
      setState({ loading: false, error: null, summary, customerId, subscriptionId, plan });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to load billing summary";
      setState({ loading: false, error: message, summary: null, customerId, subscriptionId, plan });
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const value = useMemo(
    () => ({
      ...state,
      refetch: load,
    }),
    [state, load]
  );

  return value;
}
